import { FC, useContext, useMemo, useRef, useState } from 'react';

import cn from 'classnames';
import dayjs from 'dayjs';

import { LocalizationContext } from 'localization';

import { ESessionType } from '../types';
import s from './Booking.module.scss';
import { IHostProps } from './interfaces';
import { Tooltip } from './Tooltip';

export const Booking: FC<IHostProps> = ({
  booking,
  top,
  containerWidth,
  date,
  pageRef,
  openClient,
  openBooking,
  scrollContainerRef,
}) => {
  const { t } = useContext(LocalizationContext);
  const ref = useRef<HTMLDivElement>(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null);

  const from = dayjs(booking.from);
  const to = dayjs(booking.to);
  const possibleLeft = from.diff(dayjs(date, 'DD.MM.YYYY'), 'minutes') * 2 + 45;

  const fromLeft = possibleLeft < 48;

  const left = fromLeft ? 45 : possibleLeft;
  const possibleWidth = to.diff(dayjs(booking.from), 'minutes') * 2;

  let width = possibleWidth;

  const fromRight =
    containerWidth &&
    containerWidth + 50 <
      to.diff(dayjs(date, 'DD.MM.YYYY'), 'minutes') * 2 + 45;

  if (fromLeft) {
    width = possibleWidth + possibleLeft - 45;
  }

  if (fromRight) {
    width = containerWidth - left + 5;
  }

  const isDayBooking = left + width > 765 && left + width < 2685;

  const cardClasses = useMemo<string[]>(() => {
    const className = [s.card];

    if (booking.type === ESessionType.BOOKING) className.push(s.card__booking);
    if (booking.type == ESessionType.CLIENT_SESSION)
      className.push(s.card__session);
    if (!isDayBooking && booking.type === ESessionType.BOOKING)
      className.push(s.nextDay);
    if (booking.type === ESessionType.ONLINE_BOOKING)
      className.push(s.card__booking_online);
    if (fromLeft && fromRight) {
      className.push(s.left_right);
    } else if (fromLeft) {
      className.push(s.left);
    } else if (fromRight) {
      className.push(s.right);
    }

    return className;
  }, [booking, isDayBooking, fromLeft, fromRight]);

  const defaultName = useMemo<string>(() => {
    const textsArray = [];

    if (booking.client?.nickname) {
      textsArray.push(booking.client?.nickname);
    }

    if (booking.comment) {
      textsArray.push(booking.comment);
    }

    return textsArray.length ? textsArray.join(', ') : t('client.guest');
  }, [booking]);

  return (
    <div
      ref={ref}
      className={cn(...cardClasses)}
      style={{ width, left, top }}
      onClick={() => {
        if (booking.type !== ESessionType.BOOKING) return;
        openBooking(booking.id);
        setIsTooltipVisible(false);
      }}
      onMouseEnter={() => {
        if (timerId) clearTimeout(timerId);
        setIsTooltipVisible(true);
      }}
      onMouseLeave={() => {
        const timer = setTimeout(() => setIsTooltipVisible(false), 100);
        setTimerId(timer);
      }}
    >
      {width >= 44 && (
        <>
          <span className={s.client}>{defaultName}</span>
          <span className={s.duration}>
            {from.format('HH:mm')} — {to.format('HH:mm')}
          </span>
        </>
      )}
      {isTooltipVisible && (
        <Tooltip
          scrollContainerRef={scrollContainerRef}
          openClient={openClient}
          booking={booking}
          pageRef={pageRef}
          parentRef={ref}
          isDayBooking={isDayBooking}
          close={() => setIsTooltipVisible(false)}
        />
      )}
    </div>
  );
};
